import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Store, ArrowLeft, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function RegisterSeller() {
  const { register } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    phone: "",
    seller_type: "individual",
    shop_name: "",
    city: "Kinshasa",
    country: "RDC",
    address: "",
    business_name: "",
    rccm: "",
    tax_id: "",
    activity_description: "",
    accept_terms: false,
    accept_commission: false,
    newsletter_opt_in: false,
  });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const totalSteps = form.seller_type === "business" ? 3 : 2;

  const nextStep = () => {
    if (step === 1) {
      if (!form.full_name || !form.email || !form.password || !form.phone) return toast.error(t("rs.err_fill_all"));
      if (form.password.length < 6) return toast.error(t("rs.err_password_min"));
    }
    if (step === 2) {
      if (!form.shop_name || !form.city || !form.country) return toast.error(t("rs.err_fill_all"));
    }
    setStep((s) => Math.min(s + 1, totalSteps));
  };

  const prevStep = () => setStep((s) => Math.max(1, s - 1));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.accept_terms || !form.accept_commission) {
      return toast.error(t("rs.err_terms"));
    }
    if (form.seller_type === "business" && (!form.business_name || !form.rccm)) {
      return toast.error(t("rs.err_business"));
    }
    setLoading(true);
    try {
      const payload = {
        full_name: form.full_name,
        email: form.email,
        password: form.password,
        phone: form.phone,
        city: form.city,
        country: form.country,
        role: "seller",
        newsletter_opt_in: form.newsletter_opt_in,
      };
      await register(payload);
      toast.success(t("rs.success"));
      try {
        sessionStorage.setItem("kyc_prefill", JSON.stringify({
          seller_type: form.seller_type,
          full_legal_name: form.full_name,
          phone: form.phone,
          address: form.address,
          city: form.city,
          country: form.country,
          business_name: form.business_name,
          rccm: form.rccm,
          tax_id: form.tax_id,
          shop_name: form.shop_name,
          activity_description: form.activity_description,
        }));
      } catch (e) { console.warn("KYC prefill save failed:", e?.message); }
      nav("/vendeur/verification");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("rs.err_signup"));
    } finally { setLoading(false); }
  };

  const stepOf = t("rs.step_of").replace("{step}", step).replace("{total}", totalSteps);

  return (
    <div className="max-w-[720px] mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="register-seller-page">
      <Link to="/inscription" className="inline-flex items-center gap-1 text-sm text-[#4B5563] hover:text-[#1A3322] mb-4" data-testid="back-to-choice">
        <ArrowLeft className="w-4 h-4"/>{t("reg.back")}
      </Link>

      <div className="bg-white p-6 sm:p-8 lg:p-10 rounded-2xl border-2 border-[#C85A32]/20">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-full bg-[#FFF7ED] flex items-center justify-center">
            <Store className="w-5 h-5 text-[#C85A32]"/>
          </div>
          <div>
            <h1 className="heading font-extrabold text-2xl sm:text-3xl text-[#111812]">{t("rs.title")}</h1>
            <p className="text-sm text-[#4B5563] mt-1">{stepOf}</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="flex gap-1.5 mb-8" data-testid="seller-progress">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full ${i < step ? "bg-[#C85A32]" : "bg-[#F5F5F0]"}`}/>
          ))}
        </div>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="seller-form">

          {step === 1 && (
            <div className="space-y-4" data-testid="seller-step-1">
              <p className="label-uppercase text-xs text-[#1A3322]">{t("rs.step1_label")}</p>
              <div className="space-y-2">
                <Label>{t("rs.account_type")}</Label>
                <Select value={form.seller_type} onValueChange={(v) => set("seller_type", v)}>
                  <SelectTrigger data-testid="seller-type"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">{t("rs.individual")}</SelectItem>
                    <SelectItem value="business">{t("rs.business")}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#4B5563]">{t("rs.change_later")}</p>
              </div>
              <div className="space-y-2">
                <Label>{t("rs.responsible_fullname")}</Label>
                <Input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="seller-fullname" placeholder={t("rs.responsible_placeholder")}/>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t("rs.email")}</Label>
                  <Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="seller-email" placeholder={t("rs.email_placeholder")}/>
                </div>
                <div className="space-y-2">
                  <Label>{t("rs.phone")}</Label>
                  <Input required value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="seller-phone" placeholder={t("rs.phone_placeholder")}/>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("rs.password_min")}</Label>
                <Input required type="password" minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="seller-password"/>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="seller-step-2">
              <p className="label-uppercase text-xs text-[#1A3322]">{t("rs.step2_label")}</p>
              <div className="space-y-2">
                <Label>{t("rs.shop_name")}</Label>
                <Input required value={form.shop_name} onChange={(e) => set("shop_name", e.target.value)} data-testid="seller-shop-name" placeholder={t("rs.shop_name_placeholder")}/>
                <p className="text-xs text-[#4B5563]">{t("rs.shop_name_hint")}</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t("rs.city")}</Label>
                  <Input required value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="seller-city"/>
                </div>
                <div className="space-y-2">
                  <Label>{t("rs.country")}</Label>
                  <Input required value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="seller-country"/>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("rs.address_recommend")}</Label>
                <Textarea rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="seller-address" placeholder={t("rs.address_placeholder")}/>
              </div>
              <div className="space-y-2">
                <Label>{t("rs.what_sell")}</Label>
                <Textarea rows={3} value={form.activity_description} onChange={(e) => set("activity_description", e.target.value)} data-testid="seller-activity" placeholder={t("rs.what_sell_placeholder")}/>
              </div>
            </div>
          )}

          {step === 3 && form.seller_type === "business" && (
            <div className="space-y-4" data-testid="seller-step-3">
              <p className="label-uppercase text-xs text-[#1A3322]">{t("rs.step3_label")}</p>
              <div className="space-y-2">
                <Label>{t("rs.business_name")}</Label>
                <Input required value={form.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="seller-business-name" placeholder={t("rs.business_name_placeholder")}/>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t("rs.rccm")}</Label>
                  <Input required value={form.rccm} onChange={(e) => set("rccm", e.target.value)} data-testid="seller-rccm" placeholder={t("rs.rccm_placeholder")}/>
                </div>
                <div className="space-y-2">
                  <Label>{t("rs.tax_id")}</Label>
                  <Input value={form.tax_id} onChange={(e) => set("tax_id", e.target.value)} data-testid="seller-tax-id"/>
                </div>
              </div>
            </div>
          )}

          {step === totalSteps && (
            <div className="pt-4 border-t border-[#1A3322]/10 space-y-3">
              <p className="label-uppercase text-xs text-[#1A3322]">{t("rs.confirmation")}</p>
              <div className="rounded-lg bg-[#F5F5F0] p-4 space-y-2 text-sm">
                <p className="flex items-start gap-2"><ShieldCheck className="w-4 h-4 mt-0.5 text-[#1A3322] shrink-0"/><span>{t("rs.kyc_notice_before")} <strong>{t("rs.kyc_notice_strong")}</strong> {t("rs.kyc_notice_after")}</span></p>
                <p className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[#1A3322] shrink-0"/><span>{t("rs.commission_notice_before")} <strong>{t("rs.commission_notice_strong")}</strong> {t("rs.commission_notice_after")}</span></p>
              </div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.accept_terms} onChange={(e) => set("accept_terms", e.target.checked)} className="mt-1" data-testid="accept-terms"/>
                <span>{t("rs.accept_terms_pre")} <Link to="/mentions-legales" className="underline text-[#1A3322]" target="_blank">{t("rs.terms_link")}</Link> {t("rs.accept_terms_post")}</span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.accept_commission} onChange={(e) => set("accept_commission", e.target.checked)} className="mt-1" data-testid="accept-commission"/>
                <span>{t("rs.accept_commission")}</span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer pt-1" data-testid="seller-newsletter-label">
                <input
                  type="checkbox"
                  checked={form.newsletter_opt_in}
                  onChange={(e) => set("newsletter_opt_in", e.target.checked)}
                  className="mt-1 accent-[#C85A32]"
                  data-testid="seller-newsletter-checkbox"
                />
                <span className="text-xs text-[#4B5563] leading-relaxed">{t("reg.newsletter")}</span>
              </label>
            </div>
          )}

          <div className="flex flex-wrap gap-3 pt-4">
            {step > 1 && (
              <Button type="button" variant="outline" onClick={prevStep} className="pill-btn rounded-full" data-testid="seller-prev">{t("rs.previous")}</Button>
            )}
            {step < totalSteps ? (
              <Button type="button" onClick={nextStep} className="pill-btn bg-[#C85A32] hover:bg-[#D98A2C] rounded-full h-11 ml-auto" data-testid="seller-next">{t("rs.continue")}</Button>
            ) : (
              <Button type="submit" disabled={loading} className="pill-btn bg-[#C85A32] hover:bg-[#D98A2C] rounded-full h-11 ml-auto" data-testid="seller-submit">
                {loading ? t("rs.creating") : t("rs.create_shop")}
              </Button>
            )}
          </div>

          <p className="text-sm text-center text-[#4B5563]">
            {t("rs.already")} <Link to="/connexion" className="text-[#1A3322] font-semibold" data-testid="to-login">{t("rs.login")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
